/**
 * Curated activity library for the Session Planner.
 *
 * Pre-built SLP activities organized by goal area. Clinicians can
 * browse these and tap "Add" to append them to the session plan
 * without waiting for AI generation.
 *
 * Each activity maps to the PlanActivity shape used by the AI
 * generator, so once added they behave identically (editable
 * name/duration/cues/materials).
 *
 * ESLP-117: Language split into Receptive + Expressive, library
 * expanded per QA feedback. Mirror ios/EffectiveSLP/Session/ActivityLibrary.swift
 * — keep entries in sync.
 */

import type { GoalArea } from './domain';

export interface LibraryActivity {
  id: string;
  title: string;
  goalArea: GoalArea;
  durationMin: number;
  cues: string;
  materials: string;
  instructions: string;
  /** Age range this activity is best suited for, e.g. '3-5', '5-8', '8-12'. */
  ageRange: string;
  /** Extra keywords for search (beyond title + materials). */
  keywords: string[];
}

export const ACTIVITY_LIBRARY: LibraryActivity[] = [
  // ─── Articulation ───
  {
    id: 'artic-picture-cards',
    title: 'Picture card drill',
    goalArea: 'articulation',
    durationMin: 10,
    cues: 'Verbal model, visual cue for tongue placement, tactile cue if needed',
    materials: 'Picture cards targeting the sound, mirror',
    instructions:
      'Show picture cards one at a time. Student names the picture. ' +
      'Provide immediate feedback on production. Use mirror for visual feedback. ' +
      'Target 10-15 trials per session.',
    ageRange: '3-8',
    keywords: ['drill', 'cards', 'mirror', 'speech sound'],
  },
  {
    id: 'artic-minimal-pairs',
    title: 'Minimal pairs practice',
    goalArea: 'articulation',
    durationMin: 10,
    cues: 'Auditory discrimination, verbal model, contrastive stress',
    materials: 'Minimal pair cards (e.g. ship/sip, bat/pat)',
    instructions:
      'Present minimal pairs. Student identifies which word they hear, ' +
      'then produces both words. Focus on the contrast between the two sounds.',
    ageRange: '4-10',
    keywords: ['pairs', 'discrimination', 'phonology'],
  },
  {
    id: 'artic-sentence-level',
    title: 'Sentence-level practice',
    goalArea: 'articulation',
    durationMin: 8,
    cues: 'Verbal model, self-monitoring checklist',
    materials: 'Sentence strips with target sound in multiple positions',
    instructions:
      'Student reads sentences containing the target sound. ' +
      'Start with carrier phrases ("I see a ___") then move to longer sentences. ' +
      'Encourage self-monitoring with a simple checklist.',
    ageRange: '5-12',
    keywords: ['sentences', 'carryover', 'reading'],
  },
  {
    id: 'artic-board-game',
    title: 'Board game with sound targets',
    goalArea: 'articulation',
    durationMin: 12,
    cues: 'Verbal model, visual reminder card',
    materials: 'Any board game, target word list or picture cards',
    instructions:
      'Before each turn, student produces the target word 3 times. ' +
      'Use a visual reminder card for tongue placement. ' +
      'Game provides natural motivation for multiple trials.',
    ageRange: '3-10',
    keywords: ['game', 'motivation', 'turn-taking'],
  },
  {
    id: 'artic-barrier-game',
    title: 'Barrier game',
    goalArea: 'articulation',
    durationMin: 10,
    cues: 'Verbal model, clarification requests',
    materials: 'Barrier (folder or screen), matching picture sets',
    instructions:
      'Student and clinician each have a picture set behind a barrier. ' +
      'Student describes a picture, clinician tries to find the match. ' +
      'Requires clear production for successful communication.',
    ageRange: '5-12',
    keywords: ['barrier', 'communication', 'descriptive'],
  },
  {
    id: 'artic-story-retell',
    title: 'Story retell with target sounds',
    goalArea: 'articulation',
    durationMin: 10,
    cues: 'Verbal model, story map visual',
    materials: 'Short story or picture sequence with multiple target sounds',
    instructions:
      'Read a short story together. Student retells the story ' +
      'focusing on producing target sounds correctly. ' +
      'Use a story map (beginning, middle, end) as a visual support.',
    ageRange: '5-12',
    keywords: ['story', 'narrative', 'retell'],
  },
  {
    id: 'artic-conversation',
    title: 'Conversational practice',
    goalArea: 'articulation',
    durationMin: 10,
    cues: 'Verbal cue, self-monitoring card',
    materials: 'Conversation topic cards',
    instructions:
      'Hold a 5-minute conversation on a topic. Student self-monitors ' +
      'correct production of the target sound. Cue only when production ' +
      'breaks down. Targets carryover into spontaneous speech.',
    ageRange: '6-12',
    keywords: ['conversation', 'carryover', 'spontaneous'],
  },
  {
    id: 'artic-reading-passage',
    title: 'Reading passage with targets',
    goalArea: 'articulation',
    durationMin: 8,
    cues: 'Highlighted text, verbal model',
    materials: 'Short reading passage with target sound highlighted',
    instructions:
      'Student reads a short passage aloud with the target sound ' +
      'highlighted. Highlight marks draw attention to the target ' +
      'position. Mark correct and incorrect productions on a tracking sheet.',
    ageRange: '6-12',
    keywords: ['reading', 'tracking', 'monitoring'],
  },
  {
    id: 'artic-tongue-twisters',
    title: 'Tongue twisters',
    goalArea: 'articulation',
    durationMin: 5,
    cues: 'Verbal model, rate control',
    materials: 'Tongue twister list targeting the sound',
    instructions:
      'Practice age-appropriate tongue twisters targeting the student\'s ' +
      'sound. Start slowly, increase rate as accuracy improves. ' +
      'Great for warm-up or cool-down.',
    ageRange: '6-12',
    keywords: ['warm-up', 'rate', 'fun'],
  },
  {
    id: 'artic-tic-tac-toe',
    title: 'Tic-tac-toe drill',
    goalArea: 'articulation',
    durationMin: 10,
    cues: 'Verbal model, tactile cue',
    materials: 'Tic-tac-toe board, target word list',
    instructions:
      'Student produces a target word correctly to claim a square. ' +
      'Play tic-tac-toe against the clinician. High trial count with ' +
      'built-in motivation from the game.',
    ageRange: '4-10',
    keywords: ['game', 'trials', 'drill'],
  },

  // ─── Receptive Language ───
  // Listening, understanding, following directions
  {
    id: 'recep-following-directions',
    title: 'Following directions',
    goalArea: 'receptive_language',
    durationMin: 8,
    cues: 'Verbal repetition, gesture cues, visual schedule',
    materials: 'Classroom objects, direction cards',
    instructions:
      'Give 1-step then 2-step directions ("Put the pencil on the desk, ' +
      'then close the book"). Student follows the direction. ' +
      'Increase complexity as accuracy improves.',
    ageRange: '3-8',
    keywords: ['directions', 'comprehension', 'steps'],
  },
  {
    id: 'recep-wh-questions',
    title: 'WH-question comprehension',
    goalArea: 'receptive_language',
    durationMin: 10,
    cues: 'Visual WH-cue cards, verbal repetition',
    materials: 'Picture scene, WH-question cards',
    instructions:
      'Show a picture scene. Clinician asks who, what, where, when, why ' +
      'questions. Student points to or answers. Use visual cue cards ' +
      'to support question comprehension.',
    ageRange: '4-10',
    keywords: ['questions', 'comprehension', 'wh'],
  },
  {
    id: 'recep-concept-vocabulary',
    title: 'Basic concept vocabulary',
    goalArea: 'receptive_language',
    durationMin: 10,
    cues: 'Verbal model, picture examples',
    materials: 'Object sets illustrating concepts (big/little, in/on/under, hot/cold)',
    instructions:
      'Target basic concepts. Clinician says "Put the big one ON the table." ' +
      'Student demonstrates understanding by following. Build vocabulary ' +
      'systematically across categories.',
    ageRange: '3-7',
    keywords: ['concepts', 'vocabulary', 'spatial'],
  },
  {
    id: 'recep-story-comprehension',
    title: 'Story comprehension',
    goalArea: 'receptive_language',
    durationMin: 12,
    cues: 'Story map visual, verbal prompts',
    materials: 'Short story at language level, comprehension question sheet',
    instructions:
      'Read a story. Ask literal (who, what happened) and inferential ' +
      '(how did character feel, what might happen next) questions. ' +
      'Use story map for visual support.',
    ageRange: '5-12',
    keywords: ['comprehension', 'literal', 'inferential'],
  },
  {
    id: 'recep-listening-detail',
    title: 'Listening for details',
    goalArea: 'receptive_language',
    durationMin: 10,
    cues: 'Visual organizer, verbal repetition',
    materials: 'Short paragraph, detail question cards',
    instructions:
      'Read a short paragraph twice. Student answers questions about ' +
      'specific details (name, number, color, location). Targets auditory ' +
      'memory and detail recall.',
    ageRange: '6-12',
    keywords: ['listening', 'details', 'memory'],
  },
  {
    id: 'recep-attribute-identification',
    title: 'Attribute identification',
    goalArea: 'receptive_language',
    durationMin: 8,
    cues: 'Visual attribute chart, verbal model',
    materials: 'Objects or pictures with varied attributes',
    instructions:
      'Clinician says "Find the red, round, big one." Student identifies ' +
      'the object matching all attributes. Targets receptive vocabulary ' +
      'of color, size, shape, function.',
    ageRange: '4-10',
    keywords: ['attributes', 'vocabulary', 'identification'],
  },

  // ─── Expressive Language ───
  // Talking, labeling, sentence production, narrative
  {
    id: 'expr-category-sort',
    title: 'Category sorting',
    goalArea: 'expressive_language',
    durationMin: 8,
    cues: 'Verbal prompt, visual category labels',
    materials: 'Picture cards from 2-3 categories, sorting mats',
    instructions:
      'Present mixed picture cards. Student names each item AND its ' +
      'category ("That\'s a dog — animal"). Extend: "Tell me another animal." ' +
      'Targets naming and category production.',
    ageRange: '3-7',
    keywords: ['categories', 'sorting', 'vocabulary'],
  },
  {
    id: 'expr-object-describing',
    title: 'Object describing',
    goalArea: 'expressive_language',
    durationMin: 10,
    cues: 'EET (Expanding Expression Tool) framework, visual cue card',
    materials: 'Mystery bag with objects, EET visual',
    instructions:
      'Student pulls an object from the bag and describes it using ' +
      'the EET framework: category, function, appearance, parts, ' +
      'location, what else you know.',
    ageRange: '5-12',
    keywords: ['describing', 'EET', 'vocabulary', 'attributes'],
  },
  {
    id: 'expr-narrative-retell',
    title: 'Narrative retell',
    goalArea: 'expressive_language',
    durationMin: 12,
    cues: 'Story grammar markers, visual story map',
    materials: 'Short story, story grammar visual (character, setting, problem, solution)',
    instructions:
      'Read a story together. Student retells using the story grammar ' +
      'elements: character, setting, initiating event, problem, ' +
      'attempts, resolution, ending.',
    ageRange: '5-12',
    keywords: ['narrative', 'story grammar', 'retell'],
  },
  {
    id: 'expr-vocab-in-context',
    title: 'Vocabulary in context',
    goalArea: 'expressive_language',
    durationMin: 10,
    cues: 'Context clues, student-friendly definitions',
    materials: 'Short passage with tier-2 vocabulary words',
    instructions:
      'Read a passage with 3-5 tier-2 vocabulary words. Student uses ' +
      'each word in a sentence of their own, then discusses synonyms ' +
      'and antonyms.',
    ageRange: '6-12',
    keywords: ['vocabulary', 'tier 2', 'context'],
  },
  {
    id: 'expr-sentence-formulation',
    title: 'Sentence formulation',
    goalArea: 'expressive_language',
    durationMin: 10,
    cues: 'Visual sentence frame, expansion prompts',
    materials: 'Picture scenes, sentence starter cards',
    instructions:
      'Show a picture. Student produces a sentence using a target ' +
      'structure (subject + verb + object, or "The ___ is ___ing the ___"). ' +
      'Use visual sentence frames to scaffold.',
    ageRange: '4-10',
    keywords: ['sentences', 'syntax', 'grammar'],
  },
  {
    id: 'expr-comparing-contrasting',
    title: 'Compare and contrast',
    goalArea: 'expressive_language',
    durationMin: 10,
    cues: 'Venn diagram visual, verbal model',
    materials: 'Two related items (apples/oranges, dogs/cats)',
    instructions:
      'Student describes similarities and differences using sentence ' +
      'frames ("Both have ___", "___ has ___ but ___ has ___"). ' +
      'Targets comparative language and organization.',
    ageRange: '5-12',
    keywords: ['compare', 'contrast', 'organization'],
  },
  {
    id: 'expr-personal-narrative',
    title: 'Personal narrative',
    goalArea: 'expressive_language',
    durationMin: 12,
    cues: 'Story map, transition word visuals',
    materials: 'Personal experience prompt cards',
    instructions:
      'Student tells a story about a personal experience using a story ' +
      'map (who, where, what happened, how it ended). Cue transition ' +
      'words: first, then, after that, finally.',
    ageRange: '6-12',
    keywords: ['narrative', 'personal', 'transitions'],
  },

  // ─── Fluency ───
  {
    id: 'fluency-easy-onset',
    title: 'Easy onset practice',
    goalArea: 'fluency',
    durationMin: 8,
    cues: 'Verbal model, visual breath cue, tactile cue on diaphragm',
    materials: 'Word list, mirror',
    instructions:
      'Practice starting words with gentle, relaxed phonation. ' +
      'Start with vowels, then move to words and phrases. ' +
      'Use mirror to monitor tension.',
    ageRange: '5-12',
    keywords: ['stuttering', 'fluency shaping', 'onset'],
  },
  {
    id: 'fluency-pausing',
    title: 'Pausing and phrasing',
    goalArea: 'fluency',
    durationMin: 8,
    cues: 'Visual phrase markers',
    materials: 'Reading passage with slash marks for pauses',
    instructions:
      'Read a passage with marked pause points. Student practices ' +
      'pausing at natural phrase boundaries to reduce rate and ' +
      'improve fluency.',
    ageRange: '5-12',
    keywords: ['stuttering', 'pausing', 'rate'],
  },
  {
    id: 'fluency-desensitization',
    title: 'Desensitization activities',
    goalArea: 'fluency',
    durationMin: 10,
    cues: 'Supportive counseling, pseudostuttering practice',
    materials: 'Voluntary stuttering word list',
    instructions:
      'Practice voluntary stuttering (pseudostuttering) in a safe ' +
      'environment. Discuss feelings about stuttering. Reduces fear ' +
      'and avoidance behaviors.',
    ageRange: '6-12',
    keywords: ['stuttering', 'counseling', 'voluntary'],
  },
  {
    id: 'fluency-cancellation',
    title: 'Cancellation practice',
    goalArea: 'fluency',
    durationMin: 8,
    cues: 'Verbal model, self-monitoring checklist',
    materials: 'Reading passage or conversation topic',
    instructions:
      'After a disfluency, student pauses, identifies what happened, ' +
      'and repeats the word or phrase using a fluency-shaping technique.',
    ageRange: '6-12',
    keywords: ['stuttering', 'cancellation', 'self-monitoring'],
  },
  {
    id: 'fluency-pull-out',
    title: 'Pull-out technique',
    goalArea: 'fluency',
    durationMin: 10,
    cues: 'Visual pull-out cue card',
    materials: 'Reading passage, pull-out tracking sheet',
    instructions:
      'When a student notices a disfluency, they "pull out" — slow ' +
      'down, ease through the moment, and continue smoothly. Practice ' +
      'identifying moments and applying the technique.',
    ageRange: '7-12',
    keywords: ['stuttering', 'pull-out', 'self-monitoring'],
  },
  {
    id: 'fluency-conversational-rate',
    title: 'Conversational rate shaping',
    goalArea: 'fluency',
    durationMin: 10,
    cues: 'Visual rate meter, verbal feedback',
    materials: 'Topic cards, rate tracking app',
    instructions:
      'Hold a conversation while monitoring rate using a visual ' +
      'feedback tool. Target a slower-than-natural rate that supports ' +
      'fluency. Gradually increase rate as fluency holds.',
    ageRange: '7-12',
    keywords: ['rate', 'conversation', 'shaping'],
  },

  // ─── Social ───
  {
    id: 'social-conversation-turns',
    title: 'Conversation turn-taking',
    goalArea: 'social',
    durationMin: 10,
    cues: 'Visual turn-taking counter, verbal reminder',
    materials: 'Conversation topic cards, turn-taking counter (e.g. talking stick)',
    instructions:
      'Practice conversation with a visual turn-taking system. ' +
      'Each person gets 2-3 turns before passing. Focus on staying on ' +
      'topic and asking follow-up questions.',
    ageRange: '4-12',
    keywords: ['conversation', 'turn-taking', 'pragmatics'],
  },
  {
    id: 'social-perspective',
    title: 'Perspective-taking scenarios',
    goalArea: 'social',
    durationMin: 10,
    cues: 'Verbal prompt, emotion cards',
    materials: 'Social scenario cards, emotion faces',
    instructions:
      'Present social scenarios ("Your friend dropped their lunch"). ' +
      'Discuss: How does each person feel? What could you say or do? ' +
      'Role-play appropriate responses.',
    ageRange: '5-12',
    keywords: ['perspective', 'theory of mind', 'emotions'],
  },
  {
    id: 'social-nonverbal',
    title: 'Nonverbal communication practice',
    goalArea: 'social',
    durationMin: 8,
    cues: 'Verbal label, mirror, video feedback',
    materials: 'Emotion picture cards, mirror',
    instructions:
      'Practice identifying and producing facial expressions, body ' +
      'language, and tone of voice. Use mirror and video for immediate ' +
      'feedback.',
    ageRange: '4-12',
    keywords: ['nonverbal', 'body language', 'facial expressions'],
  },
  {
    id: 'social-problem-solving',
    title: 'Social problem-solving',
    goalArea: 'social',
    durationMin: 10,
    cues: 'Problem-solving steps visual, verbal scaffold',
    materials: 'Social problem scenario cards, problem-solving steps visual',
    instructions:
      'Present a social problem. Walk through the steps: 1) What is the ' +
      'problem? 2) What are 2-3 solutions? 3) What might happen with each? ' +
      '4) Pick the best one.',
    ageRange: '5-12',
    keywords: ['problem solving', 'social scenarios', 'solutions'],
  },
  {
    id: 'social-topic-maintenance',
    title: 'Topic maintenance game',
    goalArea: 'social',
    durationMin: 8,
    cues: 'Visual topic tracker, verbal reminder',
    materials: 'Topic cards, on-topic/off-topic visual',
    instructions:
      'Pick a topic card. Student makes 3-5 on-topic comments or ' +
      'questions. Use a visual tracker to monitor staying on topic. ' +
      'Practice bridging to related topics appropriately.',
    ageRange: '5-12',
    keywords: ['topic', 'conversation', 'pragmatics'],
  },
  {
    id: 'social-asking-questions',
    title: 'Asking follow-up questions',
    goalArea: 'social',
    durationMin: 10,
    cues: 'Question cue card, verbal model',
    materials: 'Conversation partner, question stem cards',
    instructions:
      'In a conversation, student practices asking at least one ' +
      'follow-up question after each partner turn. Use question stems ' +
      '(who, what, where, when, why, how) for support.',
    ageRange: '5-12',
    keywords: ['questions', 'conversation', 'engagement'],
  },

  // ─── AAC ───
  {
    id: 'aac-core-word',
    title: 'Core word of the week',
    goalArea: 'aac',
    durationMin: 10,
    cues: 'Aided language stimulation, verbal model',
    materials: 'AAC device or core board, activity materials',
    instructions:
      'Target a core word (go, more, stop, want) throughout an activity. ' +
      'Model the word on the device every time it comes up naturally. ' +
      'Student practices finding and using the word.',
    ageRange: '2-10',
    keywords: ['AAC', 'core vocabulary', 'modeling'],
  },
  {
    id: 'aac-communication-temptation',
    title: 'Communication temptation',
    goalArea: 'aac',
    durationMin: 8,
    cues: 'Expectant pause, aided language stimulation',
    materials: 'Preferred items in clear containers, AAC device',
    instructions:
      'Place preferred items in view but out of reach. Wait expectantly ' +
      'for student to communicate (request, protest, comment). ' +
      'Honor all communication attempts immediately.',
    ageRange: '2-8',
    keywords: ['AAC', 'requesting', 'motivation'],
  },
  {
    id: 'aac-choice-making',
    title: 'Choice making with AAC',
    goalArea: 'aac',
    durationMin: 8,
    cues: 'Aided language stimulation, visual choice board',
    materials: 'Choice board or AAC device, activity options',
    instructions:
      'Present 2-3 activity options on the choice board. Student ' +
      'selects using AAC. Immediately honor the choice. ' +
      'Model "I want ___" on the device.',
    ageRange: '2-10',
    keywords: ['AAC', 'choices', 'requesting'],
  },
  {
    id: 'aac-descriptive-talk',
    title: 'Descriptive talking with AAC',
    goalArea: 'aac',
    durationMin: 10,
    cues: 'Aided language stimulation, expansion',
    materials: 'Picture scene or activity, AAC device with descriptor words',
    instructions:
      'Look at a picture scene or do an activity together. Model ' +
      'descriptive language on the device (big, red, funny). ' +
      'Expand student utterances ("dog" → "big dog").',
    ageRange: '3-10',
    keywords: ['AAC', 'describing', 'expansion'],
  },
  {
    id: 'aac-commenting',
    title: 'Commenting with AAC',
    goalArea: 'aac',
    durationMin: 8,
    cues: 'Aided language stimulation, expectant pause',
    materials: 'Engaging video or activity, AAC device',
    instructions:
      'Watch a short video or do a fun activity together. Pause ' +
      'expectantly so the student can comment using AAC ' +
      '("wow", "fun", "again"). Model commenting naturally during pauses.',
    ageRange: '2-8',
    keywords: ['AAC', 'commenting', 'engagement'],
  },
  {
    id: 'aac-storytelling',
    title: 'Storytelling with AAC',
    goalArea: 'aac',
    durationMin: 12,
    cues: 'Story grammar visual, aided language stimulation',
    materials: 'Picture book or scene, AAC device',
    instructions:
      'Retell or create a story using AAC. Model story grammar ' +
      'elements (character, setting, problem, resolution). Student ' +
      'contributes key story elements using the device.',
    ageRange: '4-12',
    keywords: ['AAC', 'narrative', 'storytelling'],
  },

  // ─── Voice ───
  {
    id: 'voice-vocal-hygiene',
    title: 'Vocal hygiene review',
    goalArea: 'voice',
    durationMin: 5,
    cues: 'Verbal checklist, visual reminder card',
    materials: 'Vocal hygiene checklist, water bottle',
    instructions:
      'Review vocal hygiene rules: drink water, no yelling, no throat ' +
      'clearing, rest voice when sick. Student self-rates their vocal ' +
      'hygiene for the week.',
    ageRange: '5-12',
    keywords: ['voice', 'hygiene', 'vocal health'],
  },
  {
    id: 'voice-resonant',
    title: 'Resonant voice exercises',
    goalArea: 'voice',
    durationMin: 8,
    cues: 'Tactile cue on lips/nose, verbal model',
    materials: 'Mirror, humming word list',
    instructions:
      'Practice humming and resonant voice (m, n, ng words). ' +
      'Feel vibration in lips and nose. Progress to words and phrases ' +
      'with forward focus.',
    ageRange: '5-12',
    keywords: ['voice', 'resonance', 'humming'],
  },
  {
    id: 'voice-pitch',
    title: 'Pitch exploration',
    goalArea: 'voice',
    durationMin: 8,
    cues: 'Visual pitch contour, verbal model',
    materials: 'Pitch visual (kazoo, slide whistle, or app)',
    instructions:
      'Explore pitch range using a visual aid. Practice gliding from ' +
      'low to high and back. Apply to functional phrases.',
    ageRange: '5-12',
    keywords: ['voice', 'pitch', 'intonation'],
  },
  {
    id: 'voice-loudness',
    title: 'Loudness variation',
    goalArea: 'voice',
    durationMin: 8,
    cues: 'Visual loudness meter, verbal model',
    materials: 'Loudness visual (thermometer or app), word list',
    instructions:
      'Practice varying loudness appropriately for the setting. ' +
      'Use a visual meter to show target loudness level. Role-play ' +
      'different environments (library, playground, classroom).',
    ageRange: '5-12',
    keywords: ['voice', 'loudness', 'volume'],
  },
  {
    id: 'voice-breath-support',
    title: 'Breath support exercises',
    goalArea: 'voice',
    durationMin: 8,
    cues: 'Visual breath indicator, verbal model',
    materials: 'Flow visual (cotton ball, pinwheel), word list',
    instructions:
      'Practice diaphragmatic breathing and breath support for speech. ' +
      'Use a visual (blow cotton ball across the table) to teach ' +
      'controlled, steady airflow.',
    ageRange: '5-12',
    keywords: ['voice', 'breath', 'support'],
  },
  {
    id: 'voice-conversation-carryover',
    title: 'Conversation carryover',
    goalArea: 'voice',
    durationMin: 10,
    cues: 'Self-monitoring card, verbal feedback',
    materials: 'Conversation topics, self-rating sheet',
    instructions:
      'Hold a conversation while self-monitoring target voice ' +
      'qualities (loudness, pitch, resonance). Use a rating sheet ' +
      'to track. Targets carryover of voice targets into spontaneous speech.',
    ageRange: '6-12',
    keywords: ['voice', 'carryover', 'conversation'],
  },

  // ─── Cognitive ───
  {
    id: 'cog-memory-strategy',
    title: 'Memory strategy practice',
    goalArea: 'cognitive',
    durationMin: 10,
    cues: 'Verbal strategy label, visual strategy card',
    materials: 'Memory game or word list, strategy cards (rehearsal, visualization, chunking)',
    instructions:
      'Teach a memory strategy (rehearsal, visualization, chunking). ' +
      'Practice with memory games or word lists. Student verbalizes ' +
      'which strategy they used.',
    ageRange: '5-12',
    keywords: ['memory', 'strategies', 'executive function'],
  },
  {
    id: 'cog-sequencing',
    title: 'Sequencing activities',
    goalArea: 'cognitive',
    durationMin: 8,
    cues: 'Verbal prompt, visual sequence cards',
    materials: 'Sequence picture cards, first/then/next/last visual',
    instructions:
      'Present sequence picture cards out of order. Student arranges ' +
      'them in logical order and tells the story. Use temporal markers: ' +
      'first, then, next, last.',
    ageRange: '4-10',
    keywords: ['sequencing', 'temporal', 'narrative'],
  },
  {
    id: 'cog-attention',
    title: 'Sustained attention tasks',
    goalArea: 'cognitive',
    durationMin: 8,
    cues: 'Verbal encouragement, timer, visual progress tracker',
    materials: 'Attention task (cancellation, sorting, simple worksheet), timer',
    instructions:
      'Complete a structured attention task for a set duration. ' +
      'Use a timer and visual progress tracker. Gradually increase ' +
      'the duration as attention improves.',
    ageRange: '5-12',
    keywords: ['attention', 'sustained', 'focus'],
  },
  {
    id: 'cog-executive-function',
    title: 'Executive function scenarios',
    goalArea: 'cognitive',
    durationMin: 10,
    cues: 'Verbal scaffold, visual planning organizer',
    materials: 'Planning scenario cards, graphic organizer',
    instructions:
      'Present a planning scenario ("You need to pack for a field trip"). ' +
      'Walk through: What do you need? What order? What could go wrong? ' +
      'Use a graphic organizer to structure the plan.',
    ageRange: '6-12',
    keywords: ['executive function', 'planning', 'organization'],
  },
  {
    id: 'cog-problem-solving',
    title: 'Problem-solving scenarios',
    goalArea: 'cognitive',
    durationMin: 10,
    cues: 'Problem-solving visual steps',
    materials: 'Problem scenario cards, problem-solving graphic organizer',
    instructions:
      'Present a problem. Student identifies the problem, generates ' +
      'solutions, predicts outcomes, picks the best one, and reflects ' +
      'on the result. Use a graphic organizer for structure.',
    ageRange: '6-12',
    keywords: ['problem solving', 'organization', 'reasoning'],
  },
  {
    id: 'cog-flexible-thinking',
    title: 'Flexible thinking games',
    goalArea: 'cognitive',
    durationMin: 8,
    cues: 'Visual category cards, verbal prompts',
    materials: 'Category-switching games, set-shifting tasks',
    instructions:
      'Practice shifting between categories or rules. Example: sort ' +
      'by color, then switch to sorting by shape. Targets cognitive ' +
      'flexibility and set-shifting.',
    ageRange: '5-12',
    keywords: ['flexibility', 'set-shifting', 'cognitive'],
  },
];

// Helpers — mirrors ios/EffectiveSLP/Session/ActivityLibrary.swift

export function filterByArea(area: GoalArea | 'all' | null): LibraryActivity[] {
  if (!area || area === 'all') return ACTIVITY_LIBRARY;
  return ACTIVITY_LIBRARY.filter((a) => a.goalArea === area);
}

export function allAreas(): GoalArea[] {
  const present = new Set(ACTIVITY_LIBRARY.map((a) => a.goalArea));
  return (['articulation', 'receptive_language', 'expressive_language', 'fluency', 'social', 'aac', 'voice', 'cognitive'] as GoalArea[])
    .filter((g) => present.has(g));
}

export function searchActivities(query: string, area: GoalArea | 'all' | null = null): LibraryActivity[] {
  const pool = filterByArea(area);
  const q = query.trim().toLowerCase();
  if (!q) return pool;
  return pool.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.materials.toLowerCase().includes(q) ||
      a.cues.toLowerCase().includes(q) ||
      a.keywords.some((k) => k.toLowerCase().includes(q))
  );
}